/**
 * Buy Score composition rule inspect view per scope §6.6.5. Atlas Day 3a P6.
 *
 * D1 (α) catalog-only confirmed: surfaces metric catalog (label, key,
 * is_available, applies_to_metric, sort_order, tooltip_text, penalty);
 * NO weight values rendered (weights live per-session in
 * buying_sessions.ui_state.weights per scope §2 engine-only principle).
 *
 * F33 trap awareness inline if any bswc row has is_available=true AND
 * applies_to_metric IS NULL — same surface as WeightComponentsInspect
 * (Atlas Day 2.5 P7) + Weight components region grid card (Atlas Day 2 P4).
 *
 * L8 cross-references WEIGHT_COMPONENTS_PROPAGATION per scope §6.6.5
 * framing to avoid duplication — Weight components region holds substrate-
 * side propagation; this rule holds downstream-decision-side propagation.
 */

import { useEffect, useState, type JSX } from 'react';
import { URL_DEV_ENGINE_EDITOR_BUY_SCORE_COMPOSITION } from '../../../lib/atlas/atlasUrls';
import { BUY_SCORE_COMPOSITION_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { supabase } from '../../../lib/supabase';
import { AlertTriangle } from 'lucide-react';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

interface BswcRow {
  id: string;
  key: string;
  applies_to_metric: string | null;
  is_available: boolean;
}

export default function BuyScoreCompositionInspect(): JSX.Element {
  const bswcFields = FIELD_CLASSIFICATIONS.buy_score_weight_components;
  const [bswcRows, setBswcRows] = useState<BswcRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('buy_score_weight_components')
      .select('id, key, applies_to_metric, is_available')
      .then((res) => {
        if (cancelled) return;
        setBswcRows((res.data ?? []) as unknown as BswcRow[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const f33TrapRows = bswcRows.filter((r) => r.is_available && r.applies_to_metric == null);

  return (
    <InspectShell
      subRegionName="Buy Score composition"
      surfaceLine="Buy Score — weighted blend of N active metrics. D1 (α) lock: catalog-only at Atlas; per-session buyer-private weighting in buying sessions."
    >
      {/* F33 trap inline warning per scope §6.6.5 + Atlas Day 2 + Day 2.5 */}
      {!loading && f33TrapRows.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-500">
            <strong>Configuration trap:</strong> {f33TrapRows.length}{' '}
            {f33TrapRows.length === 1 ? 'weight component' : 'weight components'} active but
            not mapped to a metric ({f33TrapRows.map((r) => r.key).join(', ')}). Engine
            silently zeros the contribution per Day 91a F33 framing.
          </p>
        </div>
      )}

      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.6.5 L4: judgment-prominent for metric catalog selections (
          <code>is_available</code> + <code>penalty</code>); math implementation in
          <code>calculate_profitability</code> blend formula (weighted normalize-and-sum per
          Day 68 P3.1 Q1 Path B).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Strategic at metric catalog altitude per scope §6.6.5 L5. Catalog choices express
          ISG&apos;s ranking judgment. Per-field strategic ratio:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {Object.entries(bswcFields).map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer5Nature}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>Ownership-altitude across the board per scope §6.6.5 L6:</p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {Object.entries(bswcFields).map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> —{' '}
              <span className="font-mono">{c.layer6Authority ?? 'N/A'}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.6.5 L7: <strong>fully-traced</strong> via{' '}
          <code>bswc.provenance</code> sidecar (Day 67 P2.2 substrate + Day 82 P6.1 triggers
          cover bswc).
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Per scope §6.6.5 L8: cross-references WEIGHT_COMPONENTS_PROPAGATION (substrate ×
          calculation cell) to avoid duplication — Weight components region holds substrate-
          side propagation; this rule holds downstream-decision-side propagation.
        </p>
        <p className="mt-2">
          Curated map (verified as of 2026-05-27 per scope §8 staleness marker):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {BUY_SCORE_COMPOSITION_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.6.5 L9: reversible-with-residue at catalog-membership altitude. Catalog
          changes propagate to future Buy Score calculations; past calculations in{' '}
          <code>product_profitability</code> cache preserve prior catalog membership;
          buying-session scenarios may lock to prior catalog via{' '}
          <code>param_snapshot</code>.
        </p>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.6.5 L11. Surfaces in title block sentence 1
          ranking clause (Atlas Day 1 substrate-driven render — 7 active metrics enumerated
          per current bswc state).
        </p>
      </LayerSection>

      <LayerSection layer={4 /* cross-links footer */} title="Cross-links">
        <ul className="list-disc pl-5 text-xs">
          <li>
            <a href={URL_DEV_ENGINE_EDITOR_BUY_SCORE_COMPOSITION} className="text-accent hover:underline">
              Engine Editor → Weight Components → Buy Score composition
            </a>{' '}
            (admin: bswc catalog edit surface)
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
